import _ from 'lodash';

/**
 * Presentation-only display names for opaque literal values (e.g. a channel
 * GUID reading back as "Brokered").
 *
 * Keyed by FIELD PATH rather than by type on purpose: typing the field as an
 * enum would make the validator reject a stored literal whose value is no
 * longer in the list (a channel dropped from the config), rewriting history as
 * an error. A label map is inert — an unknown value simply renders as itself.
 *
 * schemaParams.valueLabels: { '<field.path>': { '<rawValue>': '<label>' } }
 */

const stripQuotes = value => String(value ?? '').replace(/^['"]|['"]$/g, '');

/**
 * @param {Object|null} valueLabels
 * @param {string|null} fieldPath
 * @param {*} value
 * @returns {string|null} the display label, or null when there is none
 */
export const resolveLiteralLabel = (valueLabels, fieldPath, value) => {
  if (_.isNil(valueLabels) || _.isNil(fieldPath)) {
    return null;
  }

  const labelsForField = valueLabels[fieldPath];

  if (!_.isPlainObject(labelsForField)) {
    return null;
  }

  return labelsForField[stripQuotes(value)] ?? null;
};

/**
 * Labelled options for a field path, for value autocomplete. Each option
 * inserts the raw value while displaying the label.
 *
 * @param {Object|null} valueLabels
 * @param {string|null} fieldPath
 * @returns {Array<{ value: string, label: string }>}
 */
export const getLiteralLabelOptions = (valueLabels, fieldPath) => {
  if (_.isNil(valueLabels) || _.isNil(fieldPath)) {
    return [];
  }

  const labelsForField = valueLabels[fieldPath];

  if (!_.isPlainObject(labelsForField)) {
    return [];
  }

  return Object.entries(labelsForField).map(([value, label]) => ({ value, label }));
};

/**
 * Resolve the field path an operand compares against, following an iterator
 * binding so `_ == '<guid>'` inside `ANY(a.b.Channels, …)` resolves to
 * `a.b.Channels`.
 *
 * @param {Object|null} operandNode
 * @param {Object} iteratorPaths iterator name -> array field path
 * @returns {string|null}
 */
export const resolveOperandFieldPath = (operandNode, iteratorPaths = {}) => {
  const raw = operandNode?.value;

  if (!_.isString(raw)) {
    return null;
  }

  return iteratorPaths[raw] ?? raw;
};
